import React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import cx from 'classnames';

import { getInboxUnreadCount } from 'ha/modules/InboxCounter/selectors';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { NoSSR } from '@ha/dsl';
import { WeWantToPayCTA, WeWantToPayModal } from 'ha/modules/WeWantToPay';
import Alertbar from 'ha/modules/Alertbar';
import NavBar from 'ha/modules/NavBar';
import Footer from 'ha/modules/Footer';
import LoadFonts, { FontInfo } from 'ha/components/LoadFonts';
import defaultFont from 'ha/constants/fonts/default';
import { KrugerDialog } from 'ha/modules/Kruger';
import AuthModal from 'ha/modules/AuthModal';
import VerifyEmailDialog from 'ha/modules/ActivationDialogs/VerifyEmailDialog';
import { useIntl } from 'ha/i18n';
import { useLocation } from 'ha/helpers/router';
import { GoogleOneTapLazy } from 'ha/modules/GoogleOneTap/GoogleOneTap.lazy';
import StickyFooter from './StickyFooter';
import FixedFooterSection from './FixedFooterSection';
import Meta from './Meta';
import Favicons from './Favicons';
import { ProgressBar } from './ProgressBar';

interface Props {
  activeFonts?: FontInfo[];
  fixedFooterContent?: React.ReactNode;
  globalBanner?: React.ReactNode;
  hasFooter?: boolean;
  hasHeader?: boolean;
  hasProgressBar?: boolean;
  hasStickyNav?: boolean;
  isAuthenticated: boolean;
  noFollowFooter?: boolean;
  pageBanner?: React.ReactNode;
  showBottomNav?: boolean;
  showMiddleMenu?: boolean;
  showSearchBar?: boolean;
  searchBarElement?: React.ReactNode;
  showWeWantToPay?: boolean;
  showMobileDrawerMenu?: boolean;
  showLogo?: boolean;
  advertiserLogo?: string;
  showActions?: boolean;
  flexContent?: boolean;
}

const useStyles = makeStyles(() => ({
  contentContainer: {
    flexGrow: 1,
  },
  flex: {
    display: 'flex',
  },
}));

export const PageLayout: React.FC<Props> = ({
  activeFonts = [defaultFont],
  children,
  fixedFooterContent,
  globalBanner,
  hasFooter = true,
  hasHeader = true,
  hasProgressBar = true,
  hasStickyNav = false,
  isAuthenticated,
  noFollowFooter = true,
  pageBanner,
  showBottomNav = true,
  showMiddleMenu = true,
  showSearchBar = false,
  searchBarElement,
  showWeWantToPay = false,
  showMobileDrawerMenu = true,
  showLogo = true,
  showActions,
  advertiserLogo = '',
  flexContent,
}) => {
  const classes = useStyles();

  const { urlResolver } = useIntl();
  const location = useLocation();
  const unreadCount = useSelector(getInboxUnreadCount);
  const { showUnreadCount } = useFeatureFlags();
  const showCounter = showUnreadCount === 'on';

  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <StickyFooter>
      <Helmet
        titleTemplate={
          showCounter && isAuthenticated && mounted && unreadCount > 0
            ? `(${unreadCount}) %s`
            : undefined
        }
      />
      <Meta />
      <LoadFonts activeFonts={activeFonts} />
      <Favicons />
      <NoSSR>
        <Alertbar />
      </NoSSR>
      {globalBanner}
      {hasProgressBar && <ProgressBar />}
      {hasHeader && (
        <NavBar
          hasStickyNav={hasStickyNav}
          showMiddleMenu={showMiddleMenu}
          showSearchBar={showSearchBar}
          searchBarElement={searchBarElement}
          showLogo={showLogo}
          advertiserLogo={advertiserLogo}
          showActions={showActions}
          showMobileDrawerMenu={showMobileDrawerMenu}
        />
      )}
      <div>
        {pageBanner}
        {isAuthenticated && showWeWantToPay && (
          <NoSSR>
            <WeWantToPayCTA />
          </NoSSR>
        )}
      </div>
      <div
        className={cx(classes.contentContainer, {
          [classes.flex]: flexContent,
        })}
      >
        {children}
      </div>
      {hasFooter ? <Footer noFollow={noFollowFooter} /> : null}
      <NoSSR>
        <FixedFooterSection
          fixedFooterContent={fixedFooterContent}
          isAuthenticated={isAuthenticated}
          showBottomNav={showBottomNav}
        />
      </NoSSR>
      {isAuthenticated && showWeWantToPay && (
        <NoSSR>
          <WeWantToPayModal />
        </NoSSR>
      )}
      {!isAuthenticated && (
        <NoSSR>
          <AuthModal />
        </NoSSR>
      )}
      {urlResolver.isOnAccountPages(location.pathname) && (
        <NoSSR>
          <VerifyEmailDialog />
        </NoSSR>
      )}
      <NoSSR>
        <KrugerDialog />
      </NoSSR>
      <GoogleOneTapLazy />
    </StickyFooter>
  );
};

export default PageLayout;
